import * as UserActions from './user.actions';

export interface State {
  userInfo: UserInfoDto;

  // flags for evaluated roles (based on userInfo) start here:
  isDevPoi: boolean;

  isSupportPoi: boolean;
  isSupportPb: boolean;

  isAdminPb: boolean;
  isAdminPia: boolean;
  isAdminAllUc: boolean;
  isAdminDinBil: boolean;
  isAdminVgrx: boolean;

  isAuctionSellerPia: boolean;
  isAuctionSellerPb: boolean;
  isAuctionSellerAllUc: boolean;
  isAuctionSellerDinBil: boolean;
  isAuctionSellerVgrx: boolean;

  isBuyNowSellerPia: boolean;
  isBuyNowSellerPb: boolean;
  isBuyNowSellerAllUc: boolean;
  isBuyNowSellerDinBil: boolean;
  isBuyNowSellerVgrx: boolean;

  isBuyerPia: boolean;
  isBuyerPb: boolean;
  isBuyerAllUc: boolean;
  isBuyerDinBil: boolean;
  isBuyerVgrx: boolean;
  isBuyerEnforcedAuction: boolean;

  isViewerEnforcedAuction: boolean;

  isExaminerPia: boolean;
  isCustodianPb: boolean;
  isSurveyorPb: boolean;
  isClerkPb: boolean;
  isClerkBmiPb: boolean;

  // flags for country
  isAustrian: boolean;
  isGerman: boolean;

  // further convenience flags based on flags above
  hasAnyRoleForPia: boolean;
  hasAnyRoleForPb: boolean;
  hasAnyRoleForAllUc: boolean;
  hasAnyRoleForDinBil: boolean;
  hasAnyRoleForVgrx: boolean;
  hasAnyBuyerRoleOrAdmin: boolean;

  canBuyOffersPia: boolean;
  canBuyOffersPb: boolean;
  canBuyOffersAllUc: boolean;
  canBuyOffersDinBil: boolean;
  canBuyOffersVgrx: boolean;

  canSellOffersPia: boolean;
  canSellOffersPb: boolean;
  canSellOffersAllUc: boolean;
  canSellOffersDinBil: boolean;
  canSellOffersVgrx: boolean;

  local: string;
}

/**
 * The reducer acts on actions as defined below, and is the only way to update state.
 *
 * @param {State} state: the initial state of the redux sub-store
 * @param {UserAction} action: the actions that this sub-store is managing
 * @returns {any} updated state of the redux sub-store
 */
export function userReducer(state: State = {
  userInfo: undefined,
  isDevPoi: false,
  isSupportPoi: false,
  isSupportPb: false,
  isAdminPb: false,
  isAdminPia: false,
  isAdminAllUc: false,
  isAdminDinBil: false,
  isAdminVgrx: false,
  isAuctionSellerPia: false,
  isAuctionSellerPb: false,
  isAuctionSellerAllUc: false,
  isAuctionSellerDinBil: false,
  isAuctionSellerVgrx: false,
  isBuyNowSellerPia: false,
  isBuyNowSellerPb: false,
  isBuyNowSellerAllUc: false,
  isBuyNowSellerDinBil: false,
  isBuyNowSellerVgrx: false,
  isBuyerPia: false,
  isBuyerPb: false,
  isBuyerAllUc: false,
  isBuyerDinBil: false,
  isBuyerVgrx: false,
  isBuyerEnforcedAuction: false,
  isExaminerPia: false,
  isViewerEnforcedAuction: false,
  hasAnyBuyerRoleOrAdmin: false,
  isCustodianPb: false,
  isSurveyorPb: false,
  isClerkPb: false,
  isClerkBmiPb: false,
  hasAnyRoleForPia: false,
  hasAnyRoleForPb: false,
  hasAnyRoleForAllUc: false,
  hasAnyRoleForDinBil: false,
  hasAnyRoleForVgrx: false,
  canBuyOffersPia: false,
  canBuyOffersPb: false,
  canBuyOffersAllUc: false,
  canBuyOffersDinBil: false,
  canBuyOffersVgrx: false,
  canSellOffersPia: false,
  canSellOffersPb: false,
  canSellOffersAllUc: false,
  canSellOffersDinBil: false,
  canSellOffersVgrx: false,
  isAustrian: true,
  isGerman: false,
  local: 'en'
},
action: UserActions.UserAction) {
  switch (action.type) {
  case UserActions.UPDATED_USER_INFO:
    return updateStateAndEvaluateUserInfo(state, action.payload);
  case UserActions.UPDATE_USER_LOCAL:
    return updateLocal(state, action.local);
  default:
    return state;
  }
}

/**
 * Update user state with newly received user info.
 *
 * @param {State} state the user state to update
 * @param {UserInfoDto} userInfo the user info dto to update the user state with
 * @returns {State} the updated user state
 */
function updateStateAndEvaluateUserInfo(state: State, userInfo: UserInfoDto): State {
  // save updated userInfo to our user state
  state.userInfo = userInfo;

  // evaluate flags based on updated userInfo for our user state
  state.isDevPoi = state.userInfo.roles.includes(<Role>'POI_DEV');

  state.isSupportPoi = state.userInfo.roles.includes(<Role>'POI_SUPPORT');
  state.isSupportPb = state.userInfo.roles.includes(<Role>'PBV_SUPPORT');

  state.isAdminPb = state.userInfo.roles.includes(<Role>'PBV_ADMIN');
  state.isAdminPia = state.userInfo.roles.includes(<Role>'PIA_ADMIN');
  state.isAdminAllUc = state.userInfo.roles.includes(<Role>'ALL_UC_ADMIN');
  state.isAdminDinBil = state.userInfo.roles.includes(<Role>'DIN_BIL_ADMIN');
  state.isAdminVgrx = state.userInfo.roles.includes(<Role>'VGRX_ADMIN');

  state.isAuctionSellerPia = state.userInfo.roles.includes(<Role>'PIA_SELLER_AUCTION');
  state.isAuctionSellerPb = state.userInfo.roles.includes(<Role>'PBV_SELLER_AUCTION');
  state.isAuctionSellerAllUc = state.userInfo.roles.includes(<Role>'ALL_UC_SELLER_AUCTION');
  state.isAuctionSellerDinBil = state.userInfo.roles.includes(<Role>'DIN_BIL_SELLER_AUCTION');
  state.isAuctionSellerVgrx = state.userInfo.roles.includes(<Role>'VGRX_SELLER_AUCTION');

  state.isBuyNowSellerPia = state.userInfo.roles.includes(<Role>'PIA_SELLER_BUYNOW');
  state.isBuyNowSellerPb = state.userInfo.roles.includes(<Role>'PBV_SELLER_BUYNOW');
  state.isBuyNowSellerAllUc = state.userInfo.roles.includes(<Role>'ALL_UC_SELLER_BUYNOW');
  state.isBuyNowSellerDinBil = state.userInfo.roles.includes(<Role>'DIN_BIL_SELLER_BUYNOW');
  state.isBuyNowSellerVgrx = state.userInfo.roles.includes(<Role>'VGRX_SELLER_BUYNOW');

  state.isBuyerPia = state.userInfo.roles.includes(<Role>'PIA_BUYER');
  state.isBuyerPb = state.userInfo.roles.includes(<Role>'PBV_BUYER');
  state.isBuyerAllUc = state.userInfo.roles.includes(<Role>'ALL_UC_BUYER');
  state.isBuyerDinBil = state.userInfo.roles.includes(<Role>'DIN_BIL_BUYER');
  state.isBuyerVgrx = state.userInfo.roles.includes(<Role>'VGRX_BUYER');
  state.isBuyerEnforcedAuction = state.userInfo.roles.includes(<Role>'PIA_GW_BUYER_ENFORCED_AUCTION');
  state.isViewerEnforcedAuction = state.userInfo.roles.includes(<Role>'PIA_GW_VIEWER_ENFORCED_AUCTION');

  state.isExaminerPia = state.userInfo.roles.includes(<Role>'PIA_EXAMINER');
  state.isCustodianPb = state.userInfo.roles.includes(<Role>'PBV_CUSTODIAN');
  state.isSurveyorPb = state.userInfo.roles.includes(<Role>'PBV_SURVEYOR');
  state.isClerkPb = state.userInfo.roles.includes(<Role>'PBV_CLERK');
  state.isClerkBmiPb = state.userInfo.roles.includes(<Role>'PBV_CLERK_BMI');

  state.isAustrian = state.userInfo.country === <Country>'AT';
  state.isGerman = state.userInfo.country === <Country>'DE';

  // evaluate further convenience flags

  // HAS ANY ROLE FOR CHANNEL (used e.g. for displaying footer)

  // convenience flag whether user has ANY role associated with PIA (including root-roles POI_DEV and POI_SUPPORT)
  state.hasAnyRoleForPia = false;
  hasAnyRoleForPIA(state);

  // convenience flag  whether user has ANY role associated with PB (including root-roles POI_DEV and POI_SUPPORT)
  state.hasAnyRoleForPb = false;
  hasAnyRoleForPB(state);

  state.hasAnyBuyerRoleOrAdmin = false;
  hasAnyBuyerRoleOrAdmin(state);

  // convenience flag whether user has ANY role associated with ALL_UC (including root-roles POI_DEV and POI_SUPPORT)
  state.hasAnyRoleForAllUc = state.isDevPoi || state.isSupportPoi ||
      state.isAdminAllUc || state.isAuctionSellerAllUc || state.isBuyNowSellerAllUc || state.isBuyerAllUc;

  // convenience flag whether user has ANY role associated with DIN_BIL (including root-roles POI_DEV and POI_SUPPORT)
  state.hasAnyRoleForDinBil = state.isDevPoi || state.isSupportPoi ||
      state.isAdminDinBil || state.isAuctionSellerDinBil || state.isBuyNowSellerDinBil || state.isBuyerDinBil;

  // convenience flag whether user has ANY role associated with VGRX (including root-roles POI_DEV and POI_SUPPORT)
  state.hasAnyRoleForVgrx = state.isDevPoi || state.isSupportPoi ||
      state.isAdminVgrx || state.isAuctionSellerVgrx || state.isBuyNowSellerVgrx || state.isBuyerVgrx;

  // HAS ANY BUYING ROLE FOR CHANNEL (used e.g. in most buying views)

  // convenience flag whether user has buying role associated with PIA or root-role
  state.canBuyOffersPia = state.isDevPoi || state.isSupportPoi || state.isAdminPia || state.isBuyerPia || state.isBuyerEnforcedAuction || state.isViewerEnforcedAuction;

  // convenience flag whether user has buying role associated with PB or root-role
  state.canBuyOffersPb = state.isDevPoi || state.isSupportPoi || state.isAdminPb || state.isSupportPb || state.isBuyerPb;


  // convenience flag whether user has buying role associated with ALL_UC or root-role
  state.canBuyOffersAllUc = state.isDevPoi || state.isSupportPoi || state.isAdminAllUc || state.isBuyerAllUc;

  // convenience flag whether user has buying role associated with DIN_BIL or root-role
  state.canBuyOffersDinBil = state.isDevPoi || state.isSupportPoi || state.isAdminDinBil || state.isBuyerDinBil;

  // convenience flag whether user has buying role associated with VGRX or root-role
  state.canBuyOffersVgrx = state.isDevPoi || state.isSupportPoi || state.isAdminVgrx || state.isBuyerVgrx;

  // HAS ANY SELLING ROLE FOR CHANNEL (used e.g. in most selling views)

  // convenience flag whether user has selling role associated with PIA or root-role
  state.canSellOffersPia = state.isDevPoi || state.isSupportPoi || state.isAdminPia || state.isAuctionSellerPia || state.isBuyNowSellerPia || state.isViewerEnforcedAuction;


  // convenience flag whether user has selling role associated with PB or root-role
  state.canSellOffersPb = state.isDevPoi || state.isSupportPoi || state.isAdminPb || state.isSupportPb ||
      state.isAuctionSellerPb || state.isBuyNowSellerPb;

  // convenience flag whether user has selling role associated with ALL_UC or root-role
  state.canSellOffersAllUc = state.isDevPoi || state.isSupportPoi || state.isAdminAllUc || state.isAuctionSellerAllUc || state.isBuyNowSellerAllUc;

  // convenience flag whether user has selling role associated with DIN_BIL or root-role
  state.canSellOffersDinBil = state.isDevPoi || state.isSupportPoi || state.isAdminDinBil || state.isAuctionSellerDinBil || state.isBuyNowSellerDinBil;

  // convenience flag whether user has selling role associated with VGRX or root-role
  state.canSellOffersVgrx = state.isDevPoi || state.isSupportPoi || state.isAdminVgrx || state.isAuctionSellerVgrx || state.isBuyNowSellerVgrx;

  return {...state};
}

function hasAnyRoleForPIA(state: State) {
  if (state.isDevPoi || state.isSupportPoi || state.isAdminPia || state.isAuctionSellerPia ||
      state.isBuyNowSellerPia || state.isBuyerPia || state.isExaminerPia || state.isBuyerEnforcedAuction ||
      state.isViewerEnforcedAuction) {
    state.hasAnyRoleForPia = true;
  }
}

function hasAnyRoleForPB(state: State) {
  if (state.isDevPoi || state.isSupportPoi ||
      state.isSupportPb || state.isAdminPb || state.isAuctionSellerPb || state.isBuyNowSellerPb || state.isBuyerPb ||
      state.isCustodianPb || state.isSurveyorPb || state.isClerkPb || state.isClerkBmiPb) {
    state.hasAnyRoleForPb = true;
  }
}

function hasAnyBuyerRoleOrAdmin(state: State) {
  if (state.isDevPoi || state.isSupportPoi || state.isSupportPb ||
      state.isAdminPb || state.isAdminAllUc || state.isAdminPia || state.isAdminDinBil || state.isAdminVgrx ||
      state.isBuyerPb || state.isBuyerAllUc || state.isBuyerPia || state.isBuyerDinBil || state.isBuyerVgrx ||
      state.isBuyerEnforcedAuction) {
    state.hasAnyBuyerRoleOrAdmin = true;
  }
}

function updateLocal(state: State, local: string): State {
  state.local = local;
  return state;
}
